import type { ConversationItem, LineRef } from '@chatapp-inbox/shared';
import { KIND_LABEL } from '../chat/attachments';

type LastMessage = NonNullable<ConversationItem['lastMessage']>;

export const NO_NAME_LABEL = 'Contato sem nome';
export const NO_MESSAGES_LABEL = 'Sem mensagens';
export const EMPTY_TEXT_LABEL = 'Mensagem sem texto';
export const SEND_FAILURE_LABEL = 'Falha no envio';
export const NO_LINE_NAME_LABEL = 'Número sem nome';

/** Limite da prévia no JS; o corte visual de uma linha fica por conta do CSS (reticências). */
const PREVIEW_MAX_CHARS = 140;
const MS_PER_DAY = 86_400_000;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Número do dia no calendário LOCAL. Contar dias pelas partes locais (e não subtraindo
 * milissegundos) evita errar "ontem" perto da meia-noite ou em dias de 23/25 horas (horário de verão).
 */
function localDayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY);
}

let weekdayFormat: Intl.DateTimeFormat | undefined;
function weekdayName(date: Date): string {
  weekdayFormat ??= new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });
  return weekdayFormat.format(date);
}

/**
 * Horário da última mensagem na lista: hoje "HH:mm" (24h), ontem "Ontem", até 6 dias atrás o dia da
 * semana abreviado, antes disso "dd/MM/yyyy". Tudo no fuso local de quem está olhando.
 * Data no futuro (relógio do aparelho atrasado em relação ao servidor) é tratada como hoje.
 */
export function formatListTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const daysAgo = localDayNumber(now) - localDayNumber(date);
  if (daysAgo <= 0) return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  if (daysAgo === 1) return 'Ontem';
  if (daysAgo <= 6) return weekdayName(date);
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/** Nome para mostrar: nome do contato, senão o telefone, senão um texto neutro. */
export function displayName(contact: { name: string; phone: string | null }): string {
  return contact.name.trim() || contact.phone?.trim() || NO_NAME_LABEL;
}

/** Nome do número de WhatsApp: o do provedor, senão o telefone, senão um texto neutro. */
export function lineName(line: LineRef): string {
  return line.name.trim() || line.phone?.trim() || NO_LINE_NAME_LABEL;
}

/**
 * Nome do número com o telefone entre parênteses quando ele diz algo a mais (o servidor usa o
 * telefone como nome se o provedor não dá um: repeti-lo seria ruído).
 */
export function lineNameWithPhone(line: LineRef): string {
  const name = lineName(line);
  const phone = line.phone?.trim() ?? '';
  return phone && phone !== name ? `${name} (${phone})` : name;
}

/**
 * Iniciais do avatar: primeira letra da primeira e da última palavra. Palavras sem letra (partes
 * de um telefone, emojis) não contam; sem nenhuma letra devolve "" e o avatar mostra um ícone.
 */
export function initials(name: string): string {
  const letters: string[] = [];
  for (const word of name.split(/\s+/)) {
    const first = /\p{L}/u.exec(word)?.[0];
    if (first) letters.push(first.toLocaleUpperCase('pt-BR'));
  }
  if (letters.length === 0) return '';
  const first = letters[0] ?? '';
  return letters.length === 1 ? first : first + (letters[letters.length - 1] ?? '');
}

/** Quantas cores diferentes o avatar tem (uma classe CSS `toneN` para cada). */
export const AVATAR_TONE_COUNT = 8;

/** Cor estável do avatar: o mesmo contato sempre cai no mesmo tom (hash FNV-1a do id). */
export function avatarTone(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % AVATAR_TONE_COUNT;
}

/** A última mensagem é de saída e o envio falhou? */
export function hasSendFailure(lastMessage: LastMessage | null): boolean {
  return lastMessage?.direction === 'out' && lastMessage.status === 'error';
}

/**
 * Prévia de uma linha: espaços e quebras colapsados, no máximo ~140 caracteres e, nas mensagens de
 * saída, o prefixo "Você: ". O corte conta caracteres (não unidades UTF-16) para não partir emoji.
 */
export function previewText(lastMessage: LastMessage | null): string {
  if (!lastMessage) return NO_MESSAGES_LABEL;
  const prefix = lastMessage.direction === 'out' ? 'Você: ' : '';
  const collapsed = lastMessage.text.replace(/\s+/g, ' ').trim();
  // Mensagem com arquivo: o tipo ("Foto", "Documento"…), seguido da legenda se houver.
  const kind = lastMessage.attachmentKind ? KIND_LABEL[lastMessage.attachmentKind] : null;
  if (kind && !collapsed) return prefix + kind;
  if (!collapsed) return prefix + EMPTY_TEXT_LABEL;
  if (kind) return previewOf(`${prefix}${kind}: `, collapsed);

  return previewOf(prefix, collapsed);
}

/** `prefix` + o texto cortado em ~140 caracteres (conta caracteres, não unidades UTF-16). */
function previewOf(prefix: string, collapsed: string): string {
  const chars = Array.from(collapsed);
  if (chars.length <= PREVIEW_MAX_CHARS) return prefix + collapsed;
  return `${prefix}${chars.slice(0, PREVIEW_MAX_CHARS).join('').trimEnd()}…`;
}

/** Texto acessível do contador de não lidas, com singular/plural. */
export function unreadLabel(count: number): string {
  return count === 1 ? '1 mensagem não lida' : `${count} mensagens não lidas`;
}

/** Número do emblema: acima de 99 vira "99+" para não alargar o círculo. */
export function unreadBadgeText(count: number): string {
  return count > 99 ? '99+' : String(count);
}
